import React, { useEffect, useState, useCallback, useMemo } from "react";
import AdminApprovalRequestListing from "../../components/admin/AdminApprovalRequestListing"; // import the new listing component
import { useLoading } from "../../context/LoadingContext";
import guLocale from "../../locales/gu";
import AdminApprovalRequestService from "../../services/admin/AdminApprovalRequestService";
import AdminHeadService from "../../services/admin/AdminHeadService";
import AdminUserService from "../../services/admin/AdminUserService";
import { showSuccessToast, showErrorToast } from "../../utils/AlertUtils";
import ConfirmationModal from "../../components/ConfirmationModal";

const AdminApprovalRequest = () => {
  const locale = guLocale.confirmationModal;
  const localeAR = guLocale.admin.approvalRequest;
  const { setIsLoading } = useLoading();
  const [approvalRequests, setApprovalRequests] = useState([]);
  const [userData, setUserData] = useState([]);
  const [headData, setHeadData] = useState([]);
  const headFilterOptions = { showHeads: true, showUsers: true };
  const isFilterVisible = true;
  const [selectedFilters, setSelectedFilters] = useState({ user: [], head: [] });
  const [filterText, setFilterText] = useState("");
  const [showApprove, setShowApprove] = useState(false);
  const [showReject, setShowReject] = useState(false);
  const [approvalRequestId, setApprovalRequestId] = useState(null);

  const columns = [
    { name: localeAR.schoolName, selector: (row) => row?.user?.school_name, sortable: true },
    { name: localeAR.rojmelName, selector: (row) => row?.user?.rojmel_name, sortable: true },
    { name: localeAR.headName, selector: (row) => row?.ledgerDetails?.head_name, sortable: true },
    { name: localeAR.description, selector: (row) => row?.ledgerDetails?.details, sortable: true },
    { name: localeAR.transactionDate, selector: (row) => row?.ledgerDetails?.transaction_date, sortable: true },
    { name: localeAR.type, selector: (row) => {
        const type = row.action;
        if (type === "update") {
          return <span className="badge badge-success text-xs">અપડેટ</span>;
        } else {
          return <span className="badge badge-danger text-xs">કાઢી નાખો</span>;
        }
      },
      sortable: true, center: true 
    },
  ];

  const fetchAll = useCallback(async () => {
    try {
      setIsLoading(true);
      const [approvalRequestsRes, usersRes, headsRes] = await Promise.all([
        await AdminApprovalRequestService.getApprovalRequests(),
        AdminUserService.getUsers(),
        AdminHeadService.getHeads(),
      ]);
      if (approvalRequestsRes && approvalRequestsRes.statusCode === 200) {
        setApprovalRequests(approvalRequestsRes.data);
      }
      if (usersRes && usersRes.statusCode === 200) {
        setUserData(usersRes.data);
      }
      if (headsRes && headsRes.statusCode === 200 && Array.isArray(headsRes.data)) {
        const headMap = new Map();
        headsRes.data.forEach((head) => {
          const key = head.head_name.toLowerCase();
          if (!headMap.has(key)) {
            headMap.set(key, head);
          }
        });
        const uniqueHeads = Array.from(headMap.values());
        setHeadData(uniqueHeads);
      }
    } catch (error) {
      console.error("Error fetching data", error);
      showErrorToast(error.message);
    } finally {
      setIsLoading(false);
    }
  }, [setIsLoading]);

  useEffect(() => {
      fetchAll();
  }, [fetchAll]);

  const filteredData = useMemo(() => {
      return approvalRequests.filter((item) => {
        console.log(item, selectedFilters);
        const matchesSearch = Object.values(item)
          .some((value) => value?.toString().toLowerCase().includes(filterText.toLowerCase()));
  
        const matchesUser = selectedFilters.user.length === 0 || selectedFilters.user.some((u) => u.value === item.user_id);
        
        const matchesHead= selectedFilters.head.length === 0 || selectedFilters.head.some((h)=> ( h.label || '').toLowerCase().trim() === (item?.ledgerDetails?.head_name || '').toLowerCase().trim());

        return matchesSearch && matchesUser && matchesHead;
      });
  }, [approvalRequests, filterText, selectedFilters]);

  const handleFilterChange = useCallback((e) => {
      setFilterText(e.target.value); // Only update filterText state
    }, []);

  const handleApplyFilters = useCallback((filterArrays) => {
        setSelectedFilters(filterArrays);
  }, []);

  const handleApprove = (requestId) => {
    setApprovalRequestId(requestId);
    setShowApprove(true);
  };

  const handleReject = (requestId) => {
    setApprovalRequestId(requestId);
    setShowReject(true);
  };

  const confirmApprove = async () => {
    try {
      setIsLoading(true);
      const response = await AdminApprovalRequestService.approveRequest(approvalRequestId);
      if (response && response.statusCode === 200) {
        showSuccessToast(response.message);
        setApprovalRequests((prev) => prev.filter((req) => req._id !== approvalRequestId));
      }
    } catch (error) {
      showErrorToast(error.message);
    } finally {
      setShowApprove(false);
      setIsLoading(false);
    }
  };

  const confirmReject = async () => {
    try {
      setIsLoading(true);
      const response = await AdminApprovalRequestService.rejectRequest(approvalRequestId);
      if (response && response.statusCode === 200) {
        showSuccessToast(response.message);
        setApprovalRequests((prev) => prev.filter((req) => req._id !== approvalRequestId));
      }
    } catch (error) {
      showErrorToast(error.message);
    } finally {
      setShowReject(false);
      setIsLoading(false);
    }
  };

  return (
    <div>
      <AdminApprovalRequestListing handleApprove={handleApprove} 
      handleReject={handleReject} 
      handleFilterChange={handleFilterChange}
      approvalRequests={filteredData} columns={columns}
      handleApplyFilters={handleApplyFilters}
      headFilterOptions={headFilterOptions}
      isFilterVisible={isFilterVisible}
      userData={userData}
      headData={headData}
      />
      <ConfirmationModal
        show={showApprove}
        title={locale.approvalTitle}
        message={locale.approvalMessage}
        onConfirm={confirmApprove}
        onCancel={() => setShowApprove(false)}
      />
      <ConfirmationModal
        show={showReject}
        title={locale.rejectTitle}
        message={locale.rejectMessage}
        onConfirm={confirmReject}
        onCancel={() => setShowReject(false)}
      />

    </div>
  );
};

export default AdminApprovalRequest;
